import { useState, useEffect, useCallback, ChangeEvent } from 'react';
import getNodeText from 'ia-react-core/~/utils/components/_getNodeText';
import { DropListComposedProps, DropListOption } from '../DropList';

interface UseDropListSelectionOptions<Value = string, OptionValue = Value> {
  options: DropListComposedProps<Value, OptionValue>['options'];
  focusedActionIndex: number;
  selectionDelay: number;
  selectionByText: boolean;
  setFocusedActionIndex: (index: number) => void;
  onOptionFocused: (value: OptionValue) => void;
  handleValueChange: (value: OptionValue | Value, event?: ChangeEvent | { type: 'click' }, options?: {}) => void;
}

const useDropListSelection = <Value, OptionValue>({ options, focusedActionIndex, selectionDelay, selectionByText, setFocusedActionIndex, onOptionFocused, handleValueChange }: UseDropListSelectionOptions<Value, OptionValue>) => {
  const [selectionText, setSelectionText] = useState('');
  const [inputText, setInputText] = useState('');

  useEffect(() => {
    if (selectionByText && selectionText !== inputText) {
      setSelectionText(inputText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setInputText('');
      setSelectionText('');
    }, selectionDelay);
    return () => clearTimeout(timerId);
  }, [inputText, selectionDelay]);

  const findSelection = useCallback((selection: DropListOption<OptionValue>[]) => (
    selection.find(({ label }) => getNodeText(label)?.toLowerCase().startsWith(selectionText))
  ), [selectionText]);

  useEffect(() => {
    if (selectionByText && selectionText) {
      const start = focusedActionIndex;
      const selectionItem = findSelection(options.slice(start + 1)) || findSelection(options.slice(0, start));
      const newFocusedIndex = options.findIndex((option) => option === selectionItem);

      if (selectionItem) {
        setSelectionText('');
        setFocusedActionIndex(newFocusedIndex);

        if (onOptionFocused) {
          onOptionFocused(selectionItem.value);
        } else {
          handleValueChange(selectionItem.value);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionText]);

  return {
    inputText,
    setInputText,
  };
};

export default useDropListSelection;
